import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCheck,
  CheckCircle,
  ClipboardCheck,
  Clock,
  FileBadge,
  FileText,
  Loader2,
  Settings,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Separator } from "../../components/ui/separator";
import { useToast } from "../../hooks/use-toast";
import { errorMessage } from "../../utils/mutationResult";
import { useAuth } from "../../hooks/use-auth";
import { sharePendingRead } from "../../lib/sharedRead";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  deleteAllNotifications,
  deleteNotification,
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  selectLastFetched,
  selectNotifications,
  selectNotificationsLoading,
  selectUnreadCount,
} from "../../redux/slices/notificationSlice.js";

// How often to auto-refresh notifications (ms)
const REFRESH_INTERVAL_MS = 60_000;

type NotificationType =
  | "success"
  | "warning"
  | "info"
  | "system"
  | "task"
  | "audit"
  | "user"
  | "report"
  | "version"
  | "form"
  | "file"
  | "error"
  | "approval";

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case "success":
    case "audit":
      return CheckCircle;
    case "warning":
    case "error":
      return AlertTriangle;
    case "task":
    case "form":
      return ClipboardCheck;
    case "report":
    case "version":
      return FileBadge;
    case "file":
      return FileText;
    case "approval":
      return ShieldAlert;
    case "system":
      return Settings;
    case "user":
      return AlertCircle;
    default:
      return Bell;
  }
};

const getNotificationColor = (type: NotificationType) => {
  switch (type) {
    case "success":
    case "audit":
    case "version":
      return "text-emerald-600 dark:text-emerald-300";
    case "warning":
      return "text-amber-500 dark:text-amber-300";
    case "error":
      return "text-red-500 dark:text-red-300";
    case "report":
    case "file":
      return "text-blue-600 dark:text-link";
    case "task":
    case "form":
      return "text-violet-600 dark:text-violet-300";
    case "approval":
      return "text-orange-500 dark:text-orange-300";
    case "system":
    case "user":
    default:
      return "text-slate-500 dark:text-muted-foreground";
  }
};

const getIconBg = (type: NotificationType) => {
  switch (type) {
    case "success":
    case "audit":
    case "version":
      return "bg-emerald-50 dark:bg-emerald-900/40";
    case "warning":
      return "bg-amber-50 dark:bg-amber-900/40";
    case "error":
      return "bg-red-50 dark:bg-red-900/40";
    case "report":
    case "file":
      return "bg-blue-50 dark:bg-brand/40";
    case "task":
    case "form":
      return "bg-violet-50 dark:bg-violet-900/40";
    case "approval":
      return "bg-orange-50 dark:bg-orange-900/40";
    default:
      return "bg-slate-50 dark:bg-surface/40";
  }
};

export function NotificationsDropdown() {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const userId = user?._id || user?.id;
  const isOnline = useAppSelector(
    (state) => state.offline?.isOnline ?? navigator.onLine,
  );
  const [actionPending, setActionPending] = useState(false);
  const { error: showError } = useToast();
  const performAction = async (action) => {
    if (!isOnline || actionPending) return;
    setActionPending(true);
    try {
      await dispatch(action).unwrap();
    } catch (error) {
      showError("Notification action failed", errorMessage(error));
    } finally {
      setActionPending(false);
    }
  };
  useNavigate();
  const notifications = useAppSelector(selectNotifications);
  const unreadCount = useAppSelector(selectUnreadCount);
  const loading = useAppSelector(selectNotificationsLoading);
  const lastFetched = useAppSelector(selectLastFetched);

  // Fetch on mount and on interval
  const loadNotifications = useCallback(() => {
    if (!userId || !isOnline || navigator.onLine === false) return;
    sharePendingRead(dispatch, ["notifications", userId, 20], () =>
      dispatch(fetchNotifications({ limit: 20 })).unwrap(),
    ).catch(() => {});
  }, [dispatch, userId, isOnline]);

  useEffect(() => {
    if (!userId || !isOnline) return;
    loadNotifications();
    const interval = setInterval(loadNotifications, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadNotifications, userId, isOnline]);

  const handleMarkRead = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    performAction(markNotificationRead(id));
  };

  const handleMarkAllRead = () => {
    performAction(markAllNotificationsRead());
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    performAction(deleteNotification(id));
  };

  const handleDeleteAll = () => {
    performAction(deleteAllNotifications());
  };

  const handleNotificationClick = (notification: any) => {
    if (!notification.read) {
      performAction(markNotificationRead(notification._id));
    }
    // if (notification.actionUrl) {
    //   navigate(notification.actionUrl)
    // }
  };

  const formatTime = (notification: any) => {
    // Use virtual timeAgo from backend if available, otherwise compute
    if (notification.timeAgo) return notification.timeAgo;
    const diff = Date.now() - new Date(notification.createdAt).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative flex h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-accent"
          aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-96 max-w-[calc(100vw-1rem)] max-h-[min(32rem,calc(100vh-5rem))] overflow-hidden p-0 border-slate-200 bg-white shadow-xl dark:border-border dark:bg-surface"
      >
        <Card className="flex max-h-full flex-col gap-0 border-0 bg-white p-0 shadow-none dark:bg-surface">
          {/* Header */}
          <CardHeader className="shrink-0 px-4 py-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm font-semibold text-slate-800 dark:text-foreground">
                  Notifications
                </CardTitle>
                {unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white text-xs px-1.5 py-0">
                    {unreadCount}
                  </Badge>
                )}
                {loading && (
                  <Loader2 className="w-3 h-3 text-slate-400 animate-spin dark:text-muted-foreground" />
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMarkAllRead}
                    disabled={!isOnline || actionPending}
                    title={
                      !isOnline
                        ? "Connect to mark notifications as read"
                        : undefined
                    }
                    className="h-7 text-xs text-slate-500 hover:text-slate-800 dark:text-muted-foreground dark:hover:text-white flex items-center gap-1"
                  >
                    <CheckCheck className="w-3 h-3" />
                    Mark all read
                  </Button>
                )}
                {notifications.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteAll}
                    disabled={!isOnline || actionPending}
                    title={
                      !isOnline ? "Connect to delete notifications" : undefined
                    }
                    className="h-7 text-xs text-slate-400 hover:text-red-500 dark:text-muted-foreground dark:hover:text-red-300 flex items-center gap-1"
                    aria-label="Clear all notifications"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          <Separator className="mt-1" />

          {!isOnline && (
            <p
              role="status"
              className="px-4 py-2 text-xs text-amber-700 dark:text-amber-300"
            >
              Offline: notifications are read-only. Connect to mark as read,
              delete, or refresh.
            </p>
          )}

          {/* Notification list */}
          <CardContent className="max-h-[min(22.5rem,calc(100vh-10rem))] overflow-y-auto p-0">
            {notifications.length > 0 ? (
              <div>
                {notifications.map((notification: any) => {
                  const Icon = getNotificationIcon(
                    notification.type as NotificationType,
                  );
                  return (
                    <div
                      key={notification._id}
                      className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-slate-100 dark:border-border last:border-b-0 group ${
                        !notification.read
                          ? "bg-blue-50/60 hover:bg-blue-50 dark:bg-brand/50 dark:hover:bg-brand/40"
                          : "bg-white hover:bg-slate-50 dark:bg-surface dark:hover:bg-accent"
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      {/* Icon */}
                      <div
                        className={`flex-shrink-0 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center ${getIconBg(notification.type as NotificationType)}`}
                      >
                        <Icon
                          className={`w-4 h-4 ${getNotificationColor(notification.type as NotificationType)}`}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-1 mb-0.5">
                          <span
                            className={`text-sm font-medium truncate leading-tight ${
                              !notification.read
                                ? "text-slate-900 dark:text-foreground"
                                : "text-slate-700 dark:text-foreground"
                            }`}
                          >
                            {notification.title}
                          </span>
                          {!notification.read && (
                            <span className="flex-shrink-0 w-2 h-2 mt-1 rounded-full bg-blue-500" />
                          )}
                        </div>
                        <p
                          className={`text-xs leading-relaxed line-clamp-2 ${
                            !notification.read
                              ? "text-slate-600 dark:text-muted-foreground"
                              : "text-slate-500 dark:text-muted-foreground"
                          }`}
                        >
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {formatTime(notification)}
                          </span>
                          {/* Action buttons shown on hover */}
                          <div className="flex items-center gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                            {!notification.read && (
                              <button
                                onClick={(e) =>
                                  handleMarkRead(notification._id, e)
                                }
                                className="text-xs text-blue-500 hover:text-blue-700 dark:text-link dark:hover:text-link px-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                disabled={!isOnline || actionPending}
                                title={
                                  !isOnline
                                    ? "Connect to mark this notification as read"
                                    : undefined
                                }
                                aria-label="Mark as read"
                              >
                                <CheckCircle className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDelete(notification._id, e)}
                              className="text-xs text-slate-400 hover:text-red-500 dark:text-muted-foreground dark:hover:text-red-300 px-1 disabled:opacity-40 disabled:cursor-not-allowed"
                              disabled={!isOnline || actionPending}
                              title={
                                !isOnline
                                  ? "Connect to delete this notification"
                                  : undefined
                              }
                              aria-label="Delete notification"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Bell className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-500 dark:text-muted-foreground">
                  No notifications
                </p>
                <p className="text-xs text-slate-500 dark:text-muted-foreground mt-1">
                  {!isOnline
                    ? "Connect to check for new notifications."
                    : lastFetched
                      ? "You're all caught up!"
                      : "Loading..."}
                </p>
              </div>
            )}
          </CardContent>

          {/* Footer */}
          {notifications.length > 0 && (
            <>
              <Separator />
              <div className="flex shrink-0 items-center justify-between px-4 py-1">
                <span className="text-xs text-slate-500 dark:text-muted-foreground">
                  {lastFetched
                    ? `Updated ${new Date(lastFetched).toLocaleTimeString()}`
                    : ""}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadNotifications}
                  className="h-7 text-xs text-slate-500 hover:text-slate-800 dark:text-muted-foreground dark:hover:text-white"
                  disabled={loading || !isOnline}
                >
                  {loading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    "Refresh"
                  )}
                </Button>
              </div>
            </>
          )}
        </Card>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
